import React, { useMemo } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { TabComponent } from '@sde/widgets-core/src/common/TabComponent';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';
import { ExitLink } from '@sde/widgets-core/src/common/ExitLink';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import PropTypes from 'prop-types';
import { RightRailSubCard } from 'src/common/RightRailSubCard';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { getDomain } from '@web/tools/getDomain';

const Divider = styled('div', {
  width: '100%',
  borderBottom: '1px solid $gray4',
  marginBottom: '10px',
});

const ProviderContainer = styled('div', {
  padding: '15px 0px',
  borderTop: '1px solid $gray4',
  borderBottom: '1px solid $gray4',
});
const props = {
  isPaanTab: true,
};

const StyledText = styled('div', {
  padding: '40px 40px 20px 0px',
});

const StyledList = styled('ul', {
  listStyleType: 'disc',
  paddingLeft: '2em',
});

const StyledProviderName = styled('span', {
  fontWeight: 'bold',
});

const StyledButton = styled(Button, {
  marginLeft: 8,
  borderBottom: 'none !important',
  fontSize: '16px !important',
});

const ButtonContainer = styled('div', {
  paddingBottom: '20px',
});

const HEADER_CONTENTS_LAGOON_MAP = [
  'PRACTICEASSIST',
  'SECUREMESSENGERCLINICALDATASUBMISSION',
  'INDIVIDUALHEALTHRECORD',
  'CARECONDUCTORANDNOTIFICATIONOFPREGNANCY',
];

const paanParcelLoadingStyles = {
  position: 'initial',
};

const PaanLinks = React.memo(({ user, toggleModal, guid }) => {
  const {
    getHomePageContentsByArrayOfKeys,
    getHomePageContentByKey,
    getHeaderContentsByArrayOfKeys,
  } = usePPDLagoon();

  const peerToPeerKey = getFeature('NEW_PEER_TO_PEER_LINK')
    ? 'PAANPEERTOPEERREQUESTS'
    : 'PAANPEERTOPEERREQUESTSLEGACY';

  const lagoonKeys = useMemo(
    () => [
      'PAANTOOLRESOURCES',
      'PAANINTERACTIVETRAININGGUIDE',
      peerToPeerKey,
      'PAANPOLICIESANDPROCEDURES',
    ],
    [peerToPeerKey]
  );

  const priorAuthHomeContentsLinks = useMemo(() => {
    const lagoonLinks = getHomePageContentsByArrayOfKeys(lagoonKeys);
    return lagoonLinks.map((link) =>
      link?.key === 'PAANPEERTOPEERREQUESTS' && guid != null
        ? { ...link, href: `${link.href}&req_context_id=${guid}` }
        : link
    );
  }, [lagoonKeys, guid, getHomePageContentsByArrayOfKeys]);

  const quickLinks = useMemo(() => {
    const links = getHeaderContentsByArrayOfKeys(HEADER_CONTENTS_LAGOON_MAP);
    return getFeature('GC_PAAN_RIGHT_RAIL')
      ? [{ ...getHomePageContentByKey('GOLDCARDSTATUSLOOKUPTOOL') }, ...links]
      : links;
  }, [getHeaderContentsByArrayOfKeys, getFeature, getHomePageContentByKey]);

  return (
    <React.Fragment>
      <Flex
        style={{ height: '45%', padding: '0px 24px' }}
        direction="column"
        justify="space-evenly"
      >
        <RightRailSubCard
          widgetName="paan"
          railHeader="PAAN Resources"
          railLinks={priorAuthHomeContentsLinks}
          user={user}
          toggleModal={toggleModal}
        />
      </Flex>
      <Divider />
      <Flex
        style={{ height: '53%', padding: '0px 24px' }}
        direction="column"
        justify="space-evenly"
      >
        <RightRailSubCard
          railHeader="Quick Links & Tools"
          railLinks={quickLinks}
          hideDivider
          user={user}
          toggleModal={toggleModal}
        />
      </Flex>
    </React.Fragment>
  );
});

export const PaanRightRails = (user, toggleModal, guid) => [
  <PaanLinks user={user} toggleModal={toggleModal} guid={guid} />,
];

const PriorAuthMainContents = ({ user, toggleProvider }) => {
  const { getHeaderContentByKey } = usePPDLagoon();
  const { servicingProviderName } = user.provider;
  const priorAuthLink = getHeaderContentByKey('priorAuth');
  const { modalContent, buttonContent } = priorAuthLink;

  const userObj = user;
  userObj.username = userObj.username.includes('?')
    ? userObj.username.replaceAll('?', '-question-')
    : userObj.username;

  userObj.optumId = userObj.optumId.includes('?')
    ? userObj.optumId.replaceAll('?', '-question-')
    : userObj.optumId;

  const handleSSOTOPAAN = () => {
    const priorAuthParams = tokenizer.create({
      sso: 'paan',
      ...userObj,
    });
    const BASE_URL = window.location.href.replace(/\/#.*/, '');
    const exitLinkUrl = `${BASE_URL}/#/sso/${priorAuthParams}`;
    return exitLinkUrl;
  };

  return (
    <React.Fragment>
      <Heading offset={1}>Prior Authorizations & Notifications</Heading>
      <ProviderContainer>
        Currently Selected Provider:{' '}
        <StyledProviderName>{servicingProviderName}</StyledProviderName>
        <StyledButton
          data-testid="edit-provider"
          variant="link"
          size="size400"
          onClick={() => {
            toggleProvider(true);
          }}
        >
          Edit
        </StyledButton>
      </ProviderContainer>
      <StyledText>
        <p>
          Use the button below to perform the following functions for all
          standard medical and specialty prior authorizations and inpatient
          admission notifications:
        </p>
        <StyledList>
          <li>Check prior authorization/notification requirements</li>
          <li>
            Submit new prior authorizations and inpatient admission
            notifications
          </li>
          <li>Check the status of an existing request</li>
        </StyledList>
        <br />
        <p>
          Clicking this button will open the Prior Authorizations/Notifications
          tools in a new window.
        </p>
      </StyledText>
      <ButtonContainer>
        <ExitLink
          to={handleSSOTOPAAN()}
          modalContent={modalContent}
          buttonContent={buttonContent}
          customAdobeEvent={{ name: 'PAAN_5_TAB_CLICK' }}
        >
          <Button after={<IconSymbol icon="open_in_new" color="white" />}>
            Go to the Prior Authorizations/Notifications tool
          </Button>
        </ExitLink>
      </ButtonContainer>
    </React.Fragment>
  );
};

const PriorAuthTabContent = ({ user }) => {
  const paanDomain = getDomain('sde-paan', 4789);
  return (
    <React.Fragment>
      <abyss-parcel
        import="paan-tab-content@dev"
        scriptdomain={paanDomain}
        showLoading={JSON.stringify(true)}
        user={JSON.stringify(user)}
        loadingStyles={JSON.stringify(paanParcelLoadingStyles)}
        props={JSON.stringify(props)}
      />
    </React.Fragment>
  );
};

export const PriorAuthTab = ({ user, toggleProvider, toggleModal, guid }) => {
  const renderPhaseTwoContent = getFeature('PAAN_WIDGET_PHASE_2');
  return (
    <TabComponent
      component={
        renderPhaseTwoContent ? (
          <PriorAuthTabContent user={user} />
        ) : (
          <PriorAuthMainContents user={user} toggleProvider={toggleProvider} />
        )
      }
      rightRails={PaanRightRails(user, toggleModal, guid)}
      css={{
        'abyss-card-root': {
          height: '650px',
        },
      }}
      tabName="Prior Authorizations & Notifications"
    />
  );
};

PriorAuthTab.propTypes = {
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleProvider: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  guid: PropTypes.string.isRequired,
};

PriorAuthMainContents.propTypes = {
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleProvider: PropTypes.func.isRequired,
};

PriorAuthTabContent.propTypes = {
  user: PropTypes.objectOf(PropTypes.any).isRequired,
};

PaanLinks.propTypes = {
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleModal: PropTypes.func.isRequired,
  guid: PropTypes.string.isRequired,
};
